/**
 * Pure decision for the gag "Add to budget" button (buyer/supervisor/admin).
 *
 * Extracted from GagDetailPanel so the button's enablement + hint and its unit
 * test share ONE source of truth. The button must be gated on whether an
 * estimate ACTUALLY EXISTS — saved on the gag OR currently typed into the
 * estimate field — NOT on generic form dirtiness. (The earlier `!isDirty`
 * escape hatch wrongly enabled the button whenever ANY unrelated field changed,
 * e.g. ticking "Requires Risk Assessment", even with no estimate anywhere.)
 *
 * Inputs (mirroring what the component holds):
 *  - savedEstimate: the persisted gags.estimated_cost (number | null)
 *  - formEstimate:  the all-strings form value (string, '' when empty; may be
 *                   null/undefined in the read-only branch where form is null)
 *  - isDirty:       the gag form has unsaved edits (bool; hard-false when the
 *                   form is null)
 *  - busy:          a push is already in flight (bool)
 *
 * Returns { disabled, hint } where hint is a string | null.
 *  - No estimate saved AND field empty → disabled, "set an estimate" hint
 *    (ticking any unrelated field must NOT enable it).
 *  - Estimate typed but form still dirty → enabled, "save first" hint.
 *  - Estimate saved + clean → enabled, no hint.
 *  - Estimate saved + dirty → enabled, "save first" hint.
 *  - busy → disabled (hint unchanged, mirrors the label flipping to "Adding…").
 */
export function budgetPushState({ savedEstimate, formEstimate, isDirty, busy }) {
  // A typed estimate counts as present only when it's a non-empty, non-null
  // string — guards the read-only branch where form (and thus formEstimate) is
  // null, and whitespace-only input.
  const hasFormEstimate = formEstimate != null && String(formEstimate).trim() !== '';
  const estimateExists = savedEstimate != null || hasFormEstimate;

  const hint = !estimateExists
    ? 'Set an estimated cost before creating this gag’s job code'
    : isDirty
      ? 'Save your changes before adding to the budget'
      : null;

  // Only a genuinely absent estimate hard-disables; a dirty-but-present
  // estimate stays clickable so the click can fire the amber "save first"
  // pulse. A push already in flight disables regardless.
  const disabled = !!busy || !estimateExists;

  return { disabled, hint };
}
